import * as R from 'ramda';
import { getSocialBenchmarkingData } from '../../utils/getSocialBenchmarkingData';
import { sbFormatter } from '../../utils/charts/sbFormatter';
import { getDateDifference } from '../../utils/getDateDifference';
import {
    SocialAccountGroup,
    SocialBenchmarkingResponse,
} from '@app/types/internal';

type State = {
    user: {
        selectedUserClientId: string;
        selectedClientId: string;
    };
};

export type HighlightResults = {
    top: SocialAccountGroup[];
    bottom: SocialAccountGroup[];
    average: number;
    periodicity: string | null;
    data: Record<number, SocialAccountGroup>;
    totalWithAccount: number;
};

interface Store {
    setState: (state: any, overwrite?: boolean) => void;
    getState: () => any;
}

interface Params {
    metric?: 'post' | 'engagement' | 'follower' | null;
    periodicity?: 'weekly' | 'monthly' | null;
    platformIds?: number[];
    mediaTypes?: number[];
    accountTypes?: number[];
    leagueIds?: number[] | null;
    favoriteIds?: number[] | null;
    visualization?: any;
    startDate?: string | null;
    endDate?: string | null;
}

const getSocialBenchmarkingAccountGroups =
    (store: Store) =>
    async (
        state: State,
        {
            metric = null,
            periodicity = null,
            platformIds = [1, 2, 3, 23],
            mediaTypes = [1, 2, 3],
            accountTypes = [1, 2],
            leagueIds = null,
            favoriteIds = null,
            visualization = null,
            startDate = null,
            endDate = null,
        }: Params,
    ) => {
        try {
            const userClientId = state.user.selectedUserClientId;
            const clientId = state.user.selectedClientId;

            // Set filters as loading
            const loadingState = R.assocPath(
                ['clients', userClientId, 'socialBenchmarking', 'isLoading'],
                true,
                state,
            );
            store.setState(loadingState);

            if (!platformIds || !platformIds.length) {
                platformIds = [1, 2, 3];
            }

            if (!mediaTypes || !mediaTypes.length) {
                mediaTypes = [1, 2, 3];
            }

            if (!accountTypes || !accountTypes.length) {
                accountTypes = [1, 2];
            }

            let previousMetricAccessor: string | undefined;
            let currentMetricAccessor: string | undefined;

            switch (metric) {
                case 'post':
                    previousMetricAccessor = 'postCountPreviousPeriod';
                    currentMetricAccessor = 'postCountCurrentPeriod';
                    break;
                case 'engagement':
                    previousMetricAccessor = 'engagementsPreviousPeriod';
                    currentMetricAccessor = 'engagementsCurrentPeriod';
                    break;
                case 'follower':
                    previousMetricAccessor =
                        'followersCountOnPreviousPeriodEndDate';
                    currentMetricAccessor =
                        'followersCountOnCurrentPeriodEndDate';
                    break;
            }

            let periodDurationDays: number;
            if (periodicity === 'weekly') {
                periodDurationDays = 7;
            } else if (periodicity === 'monthly') {
                periodDurationDays = 30;
            } else {
                periodDurationDays = getDateDifference(startDate, endDate);
            }

            const endpointResponse: SocialBenchmarkingResponse =
                await getSocialBenchmarkingData(
                    clientId,
                    favoriteIds,
                    leagueIds,
                    periodicity,
                    mediaTypes,
                    platformIds,
                    accountTypes,
                    startDate,
                    endDate,
                    true,
                    visualization,
                );

            let accountGroupsResponse: SocialBenchmarkingResponse;

            if (visualization && visualization.length === 1) {
                accountGroupsResponse = endpointResponse;
            } else {
                const ungroupedEndpointResponse =
                    await getSocialBenchmarkingData(
                        clientId,
                        favoriteIds,
                        leagueIds,
                        periodicity,
                        mediaTypes,
                        platformIds,
                        accountTypes,
                        startDate,
                        endDate,
                        false,
                    );

                const completeEngagementRateValues =
                    ungroupedEndpointResponse.accountGroupsData.map(
                        ({ accountGroupId, engagementRate }) => ({
                            accountGroupId,
                            engagementRate,
                        }),
                    );

                accountGroupsResponse = sbFormatter(
                    endpointResponse,
                    currentMetricAccessor,
                    visualization,
                    completeEngagementRateValues,
                );
            }

            const minimumDaysForDataForTrend =
                metric === 'follower'
                    ? periodDurationDays + 1
                    : periodDurationDays * 2;

            const shouldAddTrend = ({ dataForDays }: SocialAccountGroup) =>
                dataForDays >= minimumDaysForDataForTrend;

            const accountGroupWithoutAccount = (
                accountGroupsResponse.accountGroupsData || []
            )
                .filter(ag => ag.accounts.length === 0)
                .map(socialAccountGroup => ({
                    ...socialAccountGroup,
                    itemCount: null,
                    itemCountPerDay: null,
                    rank: 'N/A' as const,
                    previousRank: 'N/A' as const,
                    trend: null,
                    newFollowers: null,
                    postVolumeChange: null,
                    engagementsPerPost: null,
                    engagementRate: null,
                }));

            // First, calculate previous period rankings
            const accountGroupsWithAccountForRanking = (
                accountGroupsResponse.accountGroupsData || []
            ).filter(ag => ag.accounts.length !== 0);

            // Sort by previous period metric to calculate previous rankings
            const previousPeriodRankings = accountGroupsWithAccountForRanking
                .sort((a, b) => {
                    const aPreviousMetric = a[previousMetricAccessor] ?? 0;
                    const bPreviousMetric = b[previousMetricAccessor] ?? 0;
                    return bPreviousMetric - aPreviousMetric;
                })
                .reduce(
                    (acc, socialAccountGroup, index) => {
                        const previousMetricValue =
                            socialAccountGroup[previousMetricAccessor];
                        acc[socialAccountGroup.accountGroupId] =
                            previousMetricValue !== null
                                ? index + 1
                                : ('N/A' as const);
                        return acc;
                    },
                    {} as Record<number, number | 'N/A'>,
                );

            const accountGroupsWithAccount = accountGroupsWithAccountForRanking
                .sort((a, b) => {
                    // Handle potential null values in the sort
                    const aMetric = a[currentMetricAccessor] ?? 0;
                    const bMetric = b[currentMetricAccessor] ?? 0;
                    return bMetric - aMetric;
                })
                .map((socialAccountGroup, index) => {
                    const hasCurrentCompletePostData =
                        !socialAccountGroup.currentPeriodPostDataIncomplete;
                    const hasPreviousCompletePostData =
                        !socialAccountGroup.previousPeriodPostDataIncomplete;
                    const hasCurrentCompleteFollowerData =
                        !socialAccountGroup.currentPeriodFollowerDataIncomplete;
                    const hasPreviousCompleteFollowerData =
                        !socialAccountGroup.previousPeriodFollowerDataIncomplete;

                    let currentMetricValue;
                    if (metric === 'post' || metric === 'engagement') {
                        currentMetricValue = hasCurrentCompletePostData
                            ? socialAccountGroup[currentMetricAccessor]
                            : null;
                    } else {
                        currentMetricValue = hasCurrentCompleteFollowerData
                            ? socialAccountGroup[currentMetricAccessor]
                            : null;
                    }
                    const previousMetricValue =
                        socialAccountGroup[previousMetricAccessor];

                    return {
                        ...socialAccountGroup,
                        itemCount: currentMetricValue,
                        itemCountPerDay:
                            currentMetricValue !== null
                                ? currentMetricValue / periodDurationDays
                                : null,
                        rank:
                            currentMetricValue !== null
                                ? index + 1
                                : ('N/A' as const),
                        previousRank:
                            previousPeriodRankings[
                                socialAccountGroup.accountGroupId
                            ] || ('N/A' as const),
                        trend: shouldAddTrend(socialAccountGroup)
                            ? previousMetricValue !== null
                                ? ((currentMetricValue - previousMetricValue) /
                                      previousMetricValue) *
                                  100
                                : null
                            : null,
                        newFollowers: hasPreviousCompleteFollowerData
                            ? socialAccountGroup.followersCountOnCurrentPeriodEndDate -
                              socialAccountGroup.followersCountOnPreviousPeriodEndDate
                            : null,
                        postVolumeChange: hasPreviousCompletePostData
                            ? socialAccountGroup.postCountCurrentPeriod -
                              socialAccountGroup.postCountPreviousPeriod
                            : null,
                        engagementsPerPost:
                            hasCurrentCompletePostData &&
                            socialAccountGroup.postCountCurrentPeriod !==
                                null &&
                            socialAccountGroup.engagementsCurrentPeriod !== null
                                ? socialAccountGroup.postCountCurrentPeriod !==
                                  0
                                    ? Math.round(
                                          socialAccountGroup.engagementsCurrentPeriod /
                                              socialAccountGroup.postCountCurrentPeriod,
                                      )
                                    : 0
                                : null,
                        engagementRate:
                            hasCurrentCompletePostData &&
                            hasCurrentCompleteFollowerData
                                ? socialAccountGroup.engagementRate
                                : null,
                        // Sets partitions values to 0 if the current metric value is null
                        ...(currentMetricValue == null &&
                        socialAccountGroup.partitions?.length
                            ? {
                                  partitions: socialAccountGroup.partitions.map(
                                      partition => ({
                                          ...partition,
                                          value: 0,
                                      }),
                                  ),
                              }
                            : {}),
                    };
                })
                .sort((a, b) => {
                    // Handle sorting ranks, placing 'N/A' at the end
                    const aRank = a.rank === 'N/A' ? Infinity : a.rank; // Assign a high value to 'N/A'
                    const bRank = b.rank === 'N/A' ? Infinity : b.rank;

                    // First sort by rank
                    if (aRank !== bRank) {
                        return Number(aRank) - Number(bRank); // Sort numerically
                    }

                    // If ranks are equal, sort by current metric
                    const aMetric = a[currentMetricAccessor] ?? 0;
                    const bMetric = b[currentMetricAccessor] ?? 0;
                    return bMetric - aMetric; // Sort metrics descending
                });

            const totalWithAccount = accountGroupsWithAccount.length;
            const accountGroups = [
                ...accountGroupsWithAccount,
                ...accountGroupWithoutAccount,
            ];

            const average =
                totalWithAccount !== 0
                    ? accountGroupsWithAccount.reduce(
                          (acc, ag) => ag[currentMetricAccessor] + acc,
                          0,
                      ) / totalWithAccount
                    : 0;

            const accountGroupsWithTrendAvailable =
                accountGroupsWithAccount.filter(
                    ag => shouldAddTrend(ag) && ag.itemCount,
                ).length;

            const newFollowersAverage =
                accountGroupsWithTrendAvailable !== 0
                    ? accountGroupsWithAccount.reduce(
                          (acc, ag) =>
                              ag.followersCountOnCurrentPeriodEndDate -
                              ag.followersCountOnPreviousPeriodEndDate +
                              acc,
                          0,
                      ) / accountGroupsWithTrendAvailable
                    : 0;

            const postVolumeChangeAverage =
                totalWithAccount !== 0
                    ? accountGroupsWithAccount.reduce(
                          (acc, ag) =>
                              ag.postCountCurrentPeriod -
                              ag.postCountPreviousPeriod +
                              acc,
                          0,
                      ) / totalWithAccount
                    : 0;

            const engagementsPerPostAverage =
                totalWithAccount !== 0
                    ? accountGroupsWithAccount
                          .filter(ag => ag.postCountCurrentPeriod !== 0)
                          .reduce(
                              (acc, ag) =>
                                  ag.engagementsCurrentPeriod /
                                      ag.postCountCurrentPeriod +
                                  acc,
                              0,
                          ) / totalWithAccount
                    : 0;

            const engagementRateAverage =
                totalWithAccount !== 0
                    ? accountGroupsWithAccount.reduce(
                          (acc, ag) => ag.engagementRate + acc,
                          0,
                      ) / totalWithAccount
                    : 0;

            const highlightResults: HighlightResults = {
                top: accountGroupsWithAccount.slice(0, 3),
                bottom: accountGroupsWithAccount.slice(-3).reverse(),
                average,
                periodicity,
                data: accountGroups.reduce<Record<number, SocialAccountGroup>>(
                    (ac, socialAccountGroup) => {
                        ac[socialAccountGroup.accountGroupId] =
                            socialAccountGroup;
                        return ac;
                    },
                    {},
                ),
                totalWithAccount,
            };

            const hasIncompleteData =
                accountGroupsWithAccount.some(ag => ag.itemCount === null) ||
                accountGroupWithoutAccount.length > 0;

            const hasNoData = accountGroupsWithAccount.length === 0;

            store.setState(
                R.evolve(
                    {
                        clients: {
                            [userClientId]: {
                                socialBenchmarking: {
                                    isLoading: R.F,
                                    accountGroups: R.always(accountGroups),
                                    average: R.always({
                                        itemCount: average,
                                        itemCountPerDay:
                                            average / periodDurationDays,
                                        newFollowers: newFollowersAverage,
                                        postVolumeChange:
                                            postVolumeChangeAverage,
                                        engagementsPerPost:
                                            engagementsPerPostAverage,
                                        engagementRate: engagementRateAverage,
                                    }),
                                    highlights: R.always(highlightResults),
                                    periodStartDate: R.always(
                                        accountGroupsResponse.startDate,
                                    ),
                                    periodEndDate: R.always(
                                        accountGroupsResponse.endDate,
                                    ),
                                    hasIncompleteData:
                                        R.always(hasIncompleteData),
                                    hasNoData: R.always(hasNoData),
                                },
                            },
                        },
                    },
                    store.getState(),
                ),
                true,
            );
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        }
    };

export default getSocialBenchmarkingAccountGroups;
